import { supabase } from '../supabase/client';
import { getBeijingToday } from '../utils/timeUtils';
import { categoryStore } from '../store/categoryStore';

export interface Order {
  id: string;
  userId: string;
  userName: string;
  userPhone?: string;
  orderNo: string;
  orgId: string;
  orgName: string;
  catId: string;
  catName: string;
  levelId: string;
  levelText: string;
  duration: 'month' | 'quarter' | 'halfYear' | 'year';
  durationLabel: string;
  price: number;
  originalPrice?: number;
  actualPrice?: number;
  pointsDeducted?: number;
  deductionAmount?: number;
  status: 'pending' | 'paid' | 'expired' | 'refunded';
  payMethod?: 'wechat' | 'alipay' | 'admin' | 'balance' | 'coupon' | 'points' | 'points_alipay';
  payTime?: string;
  expireDate: string;
  createdAt: string;
  updatedAt: string;
  source: 'package' | 'exam';
}

export interface TeacherOrder {
  id: string;
  teacherId: string;
  orderNo: string;
  orgName: string;
  catName: string;
  levelText: string;
  peopleCount: number;
  packageType: 'month' | 'quarter' | 'halfYear' | 'year';
  duration: string;
  price: number;
  actualPrice?: number;
  originalPrice?: number;
  pointsDeducted?: number;
  deductionAmount?: number;
  status: 'pending' | 'paid' | 'expired' | 'refunded' | 'cancelled';
  payMethod?: 'alipay' | 'admin' | 'points' | 'wechat' | 'coupon' | 'points_alipay';
  payTime?: string;
  expireDate: string;
  createdAt: string;
  updatedAt: string;
  packageId?: string;
  source?: string;
  teacherName?: string;
  teacherPhone?: string;
  refundAmount?: number;
  refundDestination?: string;
  refundReason?: string;
  refundedAt?: string;
}

class OrderService {
  generateOrderNo(): string {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `ORD${timestamp}${random}`;
  }

  calculateExpireDate(duration: 'month' | 'quarter' | 'halfYear' | 'year'): string {
    const now = new Date();
    const months = { month: 1, quarter: 3, halfYear: 6, year: 12 }[duration];
    now.setMonth(now.getMonth() + months);
    now.setHours(23, 59, 59, 999);
    return now.toISOString();
  }

  getDurationLabel(duration: 'month' | 'quarter' | 'halfYear' | 'year' | 'custom'): string {
    const labels = { month: '月包', quarter: '季包', halfYear: '半年包', year: '年包', custom: '自定义包' };
    return labels[duration];
  }

  getDurationDays(duration: 'month' | 'quarter' | 'halfYear' | 'year' | 'custom'): string {
    const days = { month: '30天', quarter: '90天', halfYear: '180天', year: '365天', custom: '自定义' };
    return days[duration];
  }

  async createOrder(params: {
    userId: string;
    userName: string;
    userPhone?: string;
    orgName: string;
    catName: string;
    levelText: string;
    duration: 'month' | 'quarter' | 'halfYear' | 'year';
    price: number;
    source: 'package' | 'exam';
    pointsDeducted?: number;
    deductionAmount?: number;
    role?: 'student' | 'teacher';
  }): Promise<Order> {
    const role = params.role || 'student';
    const orderNo = this.generateOrderNo();
    const expireDate = this.calculateExpireDate(params.duration);
    const actualPrice = params.price - (params.deductionAmount || 0);
    const isZeroPayment = actualPrice <= 0;

    // 统一写入 orders 表
    const { data, error } = await supabase
      .from('orders')
      .insert({
        user_id: params.userId,
        role,
        order_no: orderNo,
        duration_type: params.duration,
        price: params.price,
        original_price: params.price,
        actual_price: isZeroPayment ? 0 : actualPrice,
        points_deducted: params.pointsDeducted || 0,
        deduction_amount: params.deductionAmount || 0,
        organization: params.orgName,
        category: params.catName,
        level_text: params.levelText,
        status: isZeroPayment ? 'paid' : 'pending',
        paid_at: isZeroPayment ? new Date().toISOString() : null,
        pay_method: isZeroPayment ? 'points' : (params.pointsDeducted && params.pointsDeducted > 0 ? 'points_alipay' : null),
        source: 'self',
        package_source: 'self',
        expire_date: expireDate.split('T')[0],
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      userId: params.userId,
      userName: params.userName,
      userPhone: params.userPhone,
      orderNo: data.order_no,
      orgId: '',
      orgName: params.orgName,
      catId: '',
      catName: params.catName,
      levelId: '',
      levelText: params.levelText,
      duration: params.duration,
      durationLabel: this.getDurationLabel(params.duration),
      price: data.price,
      originalPrice: data.original_price,
      actualPrice: data.actual_price,
      pointsDeducted: data.points_deducted,
      deductionAmount: data.deduction_amount,
      status: data.status as any,
      expireDate: expireDate,
      createdAt: data.created_at,
      updatedAt: data.created_at,
      source: params.source,
    };
  }

  async createTeacherOrder(params: {
    teacherId: string;
    orgName: string;
    catName: string;
    levelText: string;
    peopleCount: number;
    packageType: 'month' | 'quarter' | 'halfYear' | 'year';
    price: number;
    pointsDeducted?: number;
    deductionAmount?: number;
  }): Promise<TeacherOrder> {
    const orderNo = this.generateOrderNo();
    const expireDate = this.calculateExpireDate(params.packageType);
    const duration = this.getDurationDays(params.packageType);
    const actualPrice = params.price - (params.deductionAmount || 0);
    const isZeroPayment = actualPrice <= 0;

    const { data, error } = await supabase
      .from('orders')
      .insert({
        user_id: params.teacherId,
        role: 'teacher',
        order_no: orderNo,
        organization: params.orgName,
        category: params.catName,
        level_text: params.levelText,
        people_count: params.peopleCount,
        duration_type: params.packageType,
        price: params.price,
        original_price: params.price,
        actual_price: isZeroPayment ? 0 : actualPrice,
        points_deducted: params.pointsDeducted || 0,
        deduction_amount: params.deductionAmount || 0,
        status: isZeroPayment ? 'paid' : 'pending',
        paid_at: isZeroPayment ? new Date().toISOString() : null,
        pay_method: isZeroPayment ? 'points' : (params.pointsDeducted && params.pointsDeducted > 0 ? 'points_alipay' : null),
        expire_date: expireDate.split('T')[0],
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      teacherId: params.teacherId,
      orderNo: data.order_no,
      orgName: data.organization,
      catName: data.category,
      levelText: data.level_text,
      peopleCount: data.people_count,
      packageType: data.duration_type as any,
      duration: this.getDurationDays(params.packageType),
      price: data.price,
      actualPrice: data.actual_price,
      originalPrice: data.original_price,
      pointsDeducted: data.points_deducted,
      deductionAmount: data.deduction_amount,
      status: data.status as any,
      expireDate: data.expire_date,
      createdAt: data.created_at,
      updatedAt: data.created_at,
    };
  }

  async getAllOrders(): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('role', 'student')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map((item: any) => this.mapDbToOrder(item)) || [];
  }

  async getUserOrders(userId: string): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map((item: any) => this.mapDbToOrder(item)) || [];
  }

  async getTeacherOrders(teacherId: string): Promise<TeacherOrder[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', teacherId)
      .eq('role', 'teacher')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map((item: any) => this.mapDbToTeacherOrder(item)) || [];
  }

  async updateOrderStatus(
    orderId: string,
    status: Order['status'],
    payMethod?: 'wechat' | 'alipay'
  ): Promise<Order | null> {
    const updates: any = { status, updated_at: new Date().toISOString() };
    if (status === 'paid') {
      updates.paid_at = new Date().toISOString();
    }
    if (payMethod) {
      updates.pay_method = payMethod;
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)
      .select()
      .single();

    if (error || !data) {
      return null;
    }
    return this.mapDbToOrder(data);
  }

  async updateTeacherOrderStatus(
    orderId: string,
    status: TeacherOrder['status'],
    payMethod?: 'alipay'
  ): Promise<TeacherOrder | null> {
    const updates: any = { status, updated_at: new Date().toISOString() };
    if (status === 'paid') {
      updates.paid_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', orderId)
      .select()
      .single();

    if (error || !data) {
      return null;
    }
    return this.mapDbToTeacherOrder(data);
  }

  async simulateWechatPay(order: Order): Promise<{ success: boolean; order: Order }> {
    await new Promise(resolve => setTimeout(resolve, 1500));
    const paidOrder = await this.updateOrderStatus(order.id, 'paid', 'wechat');
    return { success: true, order: paidOrder || order };
  }

  async checkUserHasPermission(
    userId: string,
    orgName: string,
    catName: string,
    levelName: string
  ): Promise<boolean> {
    const today = getBeijingToday();

    const { data: userPackages, error: userError } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today)
      .eq('organization', orgName)
      .eq('category', catName)
      .eq('level_text', levelName);

    if (userError) {
    }

    if (userPackages && userPackages.length > 0) {
      return true;
    }

    const { data: bindData, error: bindError } = await supabase
      .from('teacher_students')
      .select('teacher_id')
      .eq('student_id', userId)
      .maybeSingle();

    if (bindError) {
    }

    if (bindData?.teacher_id) {
      const { data: teacherPackages, error: teacherError } = await supabase
        .from('user_packages')
        .select('*')
        .eq('user_id', bindData.teacher_id)
        .eq('role', 'teacher')
        .eq('is_active', true)
        .gte('expire_date', today)
        .eq('organization', orgName)
        .eq('category', catName)
        .eq('level_text', levelName);

      if (teacherError) {
      }

      if (teacherPackages && teacherPackages.length > 0) {
        return true;
      }
    }

    return false;
  }

  private mapDbToOrder(data: any): Order {
    return {
      id: data.id,
      userId: data.user_id,
      userName: data.user_name || '',
      orderNo: data.order_no,
      orgId: data.org_id || '',
      orgName: data.org_name || data.organization || '',
      catId: data.cat_id || '',
      catName: categoryStore.getCategoryNameByCode(data.cat_name || data.category || '') || '',
      levelId: data.level_id || '',
      levelText: data.level_text || '',
      duration: data.duration_type as Order['duration'],
      durationLabel: this.getDurationLabel(data.duration_type as Order['duration']),
      price: data.price,
      originalPrice: data.original_price,
      actualPrice: data.actual_price,
      pointsDeducted: data.points_deducted,
      deductionAmount: data.deduction_amount,
      status: data.status as any,
      payMethod: data.pay_method,
      payTime: data.paid_at,
      expireDate: data.expire_date || '',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      source: 'package',
    };
  }

  private mapDbToTeacherOrder(data: any): TeacherOrder {
    return {
      id: data.id,
      teacherId: data.user_id,
      orderNo: data.order_no,
      orgName: data.organization || '',
      catName: categoryStore.getCategoryNameByCode(data.category || '') || '',
      levelText: data.level_text || '',
      peopleCount: data.people_count || 0,
      packageType: data.duration_type as TeacherOrder['packageType'],
      duration: this.getDurationDays(data.duration_type as any) || '',
      price: data.price || 0,
      actualPrice: data.actual_price,
      originalPrice: data.original_price,
      pointsDeducted: data.points_deducted,
      deductionAmount: data.deduction_amount,
      status: data.status as any,
      payMethod: data.pay_method,
      payTime: data.paid_at,
      expireDate: data.expire_date || '',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const orderService = new OrderService();